import type { CSSProperties, ReactNode } from 'react';
import { NIVEIS, type TierStatic } from '@/lib/alianca-tiers';

/* ============================================================
   Primitivos visuais compartilhados pelas telas da Aliança.
   Espelham os componentes do design handoff Claude (`alianca-helpers.jsx`).
   ============================================================ */

interface AuroraProps {
  from: string;
  to: string;
  style?: CSSProperties;
}

/** Hero radial gradient atual → próximo. */
export function Aurora({ from, to, style }: AuroraProps) {
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        background: `radial-gradient(120% 100% at 50% 0%, ${from} 0%, ${from} 35%, ${to} 100%)`,
        ...style
      }}
    />
  );
}

interface GrainProps {
  opacity?: number;
}

/** Overlay sutil que tira a "lisura" plástica dos blocos de cor. */
export function Grain({ opacity = 0.1 }: GrainProps) {
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        opacity,
        backgroundImage:
          'radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px),' +
          'radial-gradient(rgba(0,0,0,0.4) 1px, transparent 1px)',
        backgroundSize: '3px 3px, 3px 3px',
        backgroundPosition: '0 0, 1.5px 1.5px',
        mixBlendMode: 'overlay'
      }}
    />
  );
}

interface ArcStripProps {
  /** kWh acumulados na janela 90d. */
  kwh: number;
  height?: number;
  showMarker?: boolean;
  showLabels?: boolean;
}

/** Barra horizontal com 7 ticks (níveis) + marker da posição atual. */
export function ArcStrip({
  kwh,
  height = 14,
  showMarker = true,
  showLabels = true
}: ArcStripProps) {
  const total = 1800;
  const pct = Math.min((kwh / total) * 100, 100);
  const currentHex = currentHexFor(kwh);

  return (
    <div className="w-full">
      <div
        className="relative w-full overflow-visible rounded-full"
        style={{
          height,
          background: 'rgba(0,27,116,0.08)'
        }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background:
              'linear-gradient(90deg,#EE263E 0%,#DE7C00 14%,#FFB92A 28%,#00C072 42%,#0090E2 56%,#835DD0 70%,#8345BA 100%)',
            backgroundSize: pct > 0 ? `${(100 / (pct / 100)).toFixed(2)}% 100%` : '100% 100%',
            backgroundPosition: 'left center'
          }}
        />
        {NIVEIS.slice(1).map((t) => {
          const p = (t.min / total) * 100;
          const reached = kwh >= t.min;
          return (
            <span
              key={t.key}
              className="absolute"
              style={{
                left: `${p}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: 2,
                height: height + 4,
                background: reached ? 'rgba(255,255,255,0.7)' : 'rgba(0,27,116,0.18)'
              }}
            />
          );
        })}
        {showMarker && (
          <span
            className="absolute"
            style={{
              left: `${pct}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: height + 8,
              height: height + 8,
              borderRadius: '999px',
              background: '#FFFFFF',
              border: `3px solid ${currentHex}`,
              boxShadow: '0 4px 12px rgba(0,27,116,0.18)'
            }}
          />
        )}
      </div>
      {showLabels && (
        <div className="mt-1.5 flex justify-between text-[10px] font-semibold uppercase tracking-[0.04em]">
          {NIVEIS.map((t) => (
            <span
              key={t.key}
              style={{
                color: kwh >= t.min ? t.hex : 'rgba(0,27,116,0.3)'
              }}
            >
              {t.label.slice(0, 3)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function currentHexFor(kwh: number): string {
  for (let i = NIVEIS.length - 1; i >= 0; i--) {
    if (kwh >= NIVEIS[i].min) return NIVEIS[i].hex;
  }
  return NIVEIS[0].hex;
}

interface TierPillProps {
  tier: TierStatic;
  size?: 'sm' | 'md';
  onDark?: boolean;
  withDot?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Pill compacto com cor do tier + label + percentual. */
export function TierPill({
  tier,
  size = 'sm',
  onDark = false,
  withDot = true,
  className = '',
  style
}: TierPillProps) {
  const dims =
    size === 'sm'
      ? { h: 22, fs: 11, px: 9, dot: 6 }
      : { h: 28, fs: 13, px: 12, dot: 8 };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${className}`}
      style={{
        height: dims.h,
        padding: `0 ${dims.px}px`,
        background: onDark ? 'rgba(255,255,255,0.12)' : `${tier.hex}1F`,
        color: onDark ? '#F1EDE4' : tier.hex,
        fontSize: dims.fs,
        fontWeight: 600,
        letterSpacing: '-0.005em',
        ...style
      }}
    >
      {withDot && (
        <span
          className="rounded-full"
          style={{ width: dims.dot, height: dims.dot, background: tier.hex }}
        />
      )}
      {tier.label} <span style={{ opacity: 0.7, fontWeight: 500 }}>· {tier.pct}%</span>
    </span>
  );
}

interface StatBlockProps {
  label: string;
  value: ReactNode;
  hint?: string | null;
  onDark?: boolean;
}

/** Mini stat cell (label uppercase + valor grande + hint opcional). */
export function StatBlock({ label, value, hint = null, onDark = false }: StatBlockProps) {
  return (
    <div className="min-w-0">
      <div
        className="mb-1 text-[10px] font-semibold uppercase tracking-[0.06em]"
        style={{ color: onDark ? 'rgba(241,237,228,0.65)' : 'rgba(0,27,116,0.5)' }}
      >
        {label}
      </div>
      <div
        className="text-[22px] font-semibold leading-[1.1]"
        style={{
          color: onDark ? '#F1EDE4' : '#001B74',
          letterSpacing: '-0.025em'
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          className="mt-0.5 text-[11px] font-medium"
          style={{ color: onDark ? 'rgba(241,237,228,0.55)' : 'rgba(0,27,116,0.5)' }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

interface AliancaHeaderProps {
  dark?: boolean;
  mini?: boolean;
  onInfo?: () => void;
}

/** Header da aba Aliança com BowArc + label "Programa A Aliança". */
export function AliancaHeader({ dark = false, mini = false, onInfo }: AliancaHeaderProps) {
  return (
    <div
      className="relative z-[2] flex items-center justify-between"
      style={{ padding: '12px 4px 12px' }}
    >
      <div className="flex items-center gap-2.5">
        <img
          src={dark ? '/logo/logo-bow-17.png' : '/logo/logo-bow-13.png'}
          alt="Bow"
          style={{ height: 28, width: 28 }}
        />
        <div>
          <div
            className="text-[11px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: dark ? 'rgba(241,237,228,0.7)' : 'rgba(0,27,116,0.55)' }}
          >
            Programa
          </div>
          <div
            className="-mt-0.5"
            style={{
              fontFamily: 'Chillax, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: mini ? 18 : 22,
              letterSpacing: '-0.02em',
              color: dark ? '#F1EDE4' : '#001B74'
            }}
          >
            A Aliança
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onInfo}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border-0"
        style={{
          background: dark ? 'rgba(241,237,228,0.12)' : 'rgba(0,27,116,0.06)',
          color: dark ? '#F1EDE4' : '#001B74'
        }}
        aria-label="Sobre o programa"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v.01M12 11v5" />
        </svg>
      </button>
    </div>
  );
}

interface SourceTagProps {
  source: 'alianca' | 'escala';
  tierColor: string;
  onDark?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** Tag minúscula indicando se o cashback efetivo veio da Aliança ou da Escala Wallet. */
export function SourceTag({
  source,
  tierColor,
  onDark = false,
  className = '',
  style
}: SourceTagProps) {
  if (source === 'alianca') {
    return (
      <span
        className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] ${className}`}
        style={{
          background: onDark ? 'rgba(255,255,255,0.2)' : tierColor,
          color: '#FFFFFF',
          ...style
        }}
      >
        Aliança
      </span>
    );
  }
  return (
    <span
      className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.06em] ${className}`}
      style={{
        background: onDark ? 'rgba(255,255,255,0.18)' : '#001B74',
        color: onDark ? '#FFFFFF' : '#F1EDE4',
        ...style
      }}
    >
      Escala
    </span>
  );
}

interface CashbackDynamicNoteProps {
  tier: TierStatic;
}

/** Texto curto explicando MAX(Aliança, Escala) na tela de recarga. */
export function CashbackDynamicNote({ tier }: CashbackDynamicNoteProps) {
  return (
    <div
      className="rounded-xl px-3.5 py-2.5 text-[11px] leading-[1.45]"
      style={{ background: 'rgba(0,27,116,0.04)', color: 'rgba(0,27,116,0.7)' }}
    >
      <strong style={{ color: tier.hex }}>Aliança</strong> garante {tier.pct}% em qualquer
      valor. A partir de <strong>R$ 250</strong>, se o cashback escalonado for maior, ele assume.
    </div>
  );
}
